import log from './log.js';
function decodeHtmlEntities(text) {
    return text
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}
export function parseTheme(themeXml) {
    const theme = {
        colors: {
            dk1: '000000',
            lt1: 'FFFFFF',
            dk2: '595959',
            lt2: 'EEEEEE',
            accent1: '4472C4',
            accent2: 'ED7D31',
            accent3: 'A5A5A5',
            accent4: 'FFC000',
            accent5: '5B9BD5',
            accent6: '70AD47',
        },
        fonts: {
            majorFont: 'Arial',
            minorFont: 'Arial',
        },
    };
    const colorSchemeMatch = themeXml.match(/<a:clrScheme[^>]*>(.*?)<\/a:clrScheme>/s);
    if (colorSchemeMatch) {
        const colorScheme = colorSchemeMatch[1];
        const extractColor = (tagName) => {
            const tagMatch = colorScheme.match(new RegExp(`<a:${tagName}>(.*?)<\/a:${tagName}>`, 's'));
            if (!tagMatch) {
                return theme.colors[tagName];
            }
            const tagContent = tagMatch[1];
            const srgbMatch = tagContent.match(/<a:srgbClr val="([0-9A-Fa-f]{6})"/);
            if (srgbMatch) {
                return srgbMatch[1];
            }
            const sysClrMatch = tagContent.match(/<a:sysClr[^>]*lastClr="([0-9A-Fa-f]{6})"/);
            if (sysClrMatch) {
                return sysClrMatch[1];
            }
            return theme.colors[tagName];
        };
        theme.colors.dk1 = extractColor('dk1');
        theme.colors.lt1 = extractColor('lt1');
        theme.colors.dk2 = extractColor('dk2');
        theme.colors.lt2 = extractColor('lt2');
        theme.colors.accent1 = extractColor('accent1');
        theme.colors.accent2 = extractColor('accent2');
        theme.colors.accent3 = extractColor('accent3');
        theme.colors.accent4 = extractColor('accent4');
        theme.colors.accent5 = extractColor('accent5');
        theme.colors.accent6 = extractColor('accent6');
    }
    const fontSchemeMatch = themeXml.match(/<a:fontScheme[^>]*>(.*?)<\/a:fontScheme>/s);
    if (fontSchemeMatch) {
        const fontScheme = fontSchemeMatch[1];
        const majorFontMatch = fontScheme.match(/<a:majorFont>.*?<a:latin typeface="([^"]+)"/s);
        if (majorFontMatch) {
            theme.fonts.majorFont = majorFontMatch[1];
        }
        const minorFontMatch = fontScheme.match(/<a:minorFont>.*?<a:latin typeface="([^"]+)"/s);
        if (minorFontMatch) {
            theme.fonts.minorFont = minorFontMatch[1];
        }
    }
    return theme;
}
export function resolveThemeColor(colorRef, theme) {
    const colorMap = {
        'tx1': 'dk1',
        'tx2': 'dk2',
        'bg1': 'lt1',
        'bg2': 'lt2',
        'dk1': 'dk1',
        'dk2': 'dk2',
        'lt1': 'lt1',
        'lt2': 'lt2',
        'accent1': 'accent1',
        'accent2': 'accent2',
        'accent3': 'accent3',
        'accent4': 'accent4',
        'accent5': 'accent5',
        'accent6': 'accent6',
    };
    const themeColorKey = colorMap[colorRef];
    if (themeColorKey) {
        const color = theme.colors[themeColorKey];
        return color.startsWith('#') ? color : '#' + color;
    }
    return '#595959';
}
function applyColorTransforms(baseColor, transformsXml) {
    const hex = baseColor.startsWith('#') ? baseColor.slice(1) : baseColor;
    let r = parseInt(hex.slice(0, 2), 16) / 255;
    let g = parseInt(hex.slice(2, 4), 16) / 255;
    let b = parseInt(hex.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            case b:
                h = ((r - g) / d + 4) / 6;
                break;
        }
    }
    const lumModMatch = transformsXml.match(/<a:lumMod val="(\d+)"\/>/);
    if (lumModMatch) {
        const lumMod = parseInt(lumModMatch[1]) / 100000;
        l = l * lumMod;
    }
    const lumOffMatch = transformsXml.match(/<a:lumOff val="(-?\d+)"\/>/);
    if (lumOffMatch) {
        const lumOff = parseInt(lumOffMatch[1]) / 100000;
        l = Math.max(0, Math.min(1, l + lumOff));
    }
    const tintMatch = transformsXml.match(/<a:tint val="(\d+)"\/>/);
    if (tintMatch) {
        const tint = parseInt(tintMatch[1]) / 100000;
        l = l + (1 - l) * tint;
    }
    const shadeMatch = transformsXml.match(/<a:shade val="(\d+)"\/>/);
    if (shadeMatch) {
        const shade = parseInt(shadeMatch[1]) / 100000;
        l = l * shade;
    }
    const hslToRgb = (h, s, l) => {
        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        }
        else {
            const hue2rgb = (p, q, t) => {
                if (t < 0)
                    t += 1;
                if (t > 1)
                    t -= 1;
                if (t < 1 / 6)
                    return p + (q - p) * 6 * t;
                if (t < 1 / 2)
                    return q;
                if (t < 2 / 3)
                    return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }
        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255),
        };
    };
    const rgb = hslToRgb(h, s, l);
    const alphaMatch = transformsXml.match(/<a:alpha val="(\d+)"\/>/);
    if (alphaMatch) {
        const alpha = parseInt(alphaMatch[1]) / 100000;
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
    const toHex = (n) => {
        const hex = Math.max(0, Math.min(255, n)).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}
export function extractTextColor(rPrXml, theme) {
    const gradFillMatch = rPrXml.match(/<a:gradFill[^>]*>(.*?)<\/a:gradFill>/s);
    if (gradFillMatch) {
        return parseGradientFill(gradFillMatch[1], theme);
    }
    const srgbMatch = rPrXml.match(/<a:solidFill>.*?<a:srgbClr val="([0-9A-Fa-f]{6})"/s);
    if (srgbMatch) {
        const color = srgbMatch[1];
        return color.startsWith('#') ? color : '#' + color;
    }
    const schemeClrFullMatch = rPrXml.match(/<a:solidFill>.*?<a:schemeClr val="([^"]+)"[^>]*>(.*?)<\/a:schemeClr>/s);
    if (schemeClrFullMatch) {
        const baseColor = resolveThemeColor(schemeClrFullMatch[1], theme);
        const transforms = schemeClrFullMatch[2];
        if (transforms && transforms.includes('<a:')) {
            return applyColorTransforms(baseColor, transforms);
        }
        return baseColor;
    }
    const schemeClrMatch = rPrXml.match(/<a:solidFill>.*?<a:schemeClr val="([^"]+)"\/>/s);
    if (schemeClrMatch) {
        return resolveThemeColor(schemeClrMatch[1], theme);
    }
    return null;
}
export function getDefaultTextColor(placeholderType, theme, slideBackground, shapeFill) {
    if (shapeFill && typeof shapeFill === 'string') {
        const fillHex = shapeFill.startsWith('#') ? shapeFill : '#' + shapeFill;
        const isDarkFill = isColorDark(fillHex);
        if (isDarkFill) {
            const color = theme.colors.lt1;
            return color.startsWith('#') ? color : '#' + color;
        }
        else {
            const color = theme.colors.dk1;
            return color.startsWith('#') ? color : '#' + color;
        }
    }
    if (slideBackground && typeof slideBackground === 'string') {
        const bgHex = slideBackground.startsWith('#') ? slideBackground : '#' + slideBackground;
        const isDarkBg = isColorDark(bgHex);
        if (isDarkBg) {
            const color = theme.colors.lt1;
            return color.startsWith('#') ? color : '#' + color;
        }
        else {
            if (placeholderType === 'title' || placeholderType === 'ctrTitle') {
                const color = theme.colors.accent1;
                return color.startsWith('#') ? color : '#' + color;
            }
            const color = theme.colors.dk1;
            return color.startsWith('#') ? color : '#' + color;
        }
    }
    if (placeholderType === 'subTitle') {
        const color = theme.colors.lt1;
        return color.startsWith('#') ? color : '#' + color;
    }
    if (placeholderType === 'title' || placeholderType === 'ctrTitle') {
        const color = theme.colors.accent1;
        return color.startsWith('#') ? color : '#' + color;
    }
    if (placeholderType === 'body' || placeholderType === 'obj' || placeholderType === null) {
        const color = theme.colors.dk1;
        return color.startsWith('#') ? color : '#' + color;
    }
    const color = theme.colors.dk1;
    return color.startsWith('#') ? color : '#' + color;
}
export function isColorDark(hexColor) {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (0.2126 * r + 0.7152 * g + 0.0722 * b);
    return brightness < 128;
}
export function extractShapeFill(spPrXml, theme) {
    const hasNoFillInSolidFill = /<(a:)?solidFill>\s*<(a:)?noFill/.test(spPrXml);
    if (hasNoFillInSolidFill) {
        return null;
    }
    const gradFillMatch = spPrXml.match(/<a:gradFill[^>]*>(.*?)<\/a:gradFill>/s);
    if (gradFillMatch) {
        return parseGradientFill(gradFillMatch[1], theme);
    }
    const srgbMatch = spPrXml.match(/<a:solidFill>.*?<a:srgbClr val="([0-9A-Fa-f]{6})"/s);
    if (srgbMatch) {
        const solidFillIndex = spPrXml.indexOf(srgbMatch[0]);
        const rPrIndex = spPrXml.indexOf('<a:rPr');
        if (rPrIndex === -1 || solidFillIndex < rPrIndex) {
            const color = srgbMatch[1];
            return color.startsWith('#') ? color : '#' + color;
        }
    }
    const schemeClrFullMatch = spPrXml.match(/<a:solidFill>.*?<a:schemeClr val="([^"]+)"[^>]*>(.*?)<\/a:schemeClr>/s);
    if (schemeClrFullMatch) {
        const solidFillIndex = spPrXml.indexOf(schemeClrFullMatch[0]);
        const rPrIndex = spPrXml.indexOf('<a:rPr');
        if (rPrIndex === -1 || solidFillIndex < rPrIndex) {
            const baseColor = resolveThemeColor(schemeClrFullMatch[1], theme);
            const transforms = schemeClrFullMatch[2];
            if (transforms && transforms.includes('<a:')) {
                return applyColorTransforms(baseColor, transforms);
            }
            return baseColor;
        }
    }
    const schemeClrMatch = spPrXml.match(/<a:solidFill>.*?<a:schemeClr val="([^"]+)"\s*\/>/s);
    if (schemeClrMatch) {
        const solidFillIndex = spPrXml.indexOf(schemeClrMatch[0]);
        const rPrIndex = spPrXml.indexOf('<a:rPr');
        if (rPrIndex === -1 || solidFillIndex < rPrIndex) {
            return resolveThemeColor(schemeClrMatch[1], theme);
        }
    }
    const fillRefMatch = spPrXml.match(/<a:fillRef[^>]*>.*?<a:schemeClr val="([^"]+)"/s);
    if (fillRefMatch) {
        return resolveThemeColor(fillRefMatch[1], theme);
    }
    return null;
}
function parseGradientFill(gradFillXml, theme) {
    const linMatch = gradFillXml.match(/<a:lin[^>]*ang="(\d+)"/);
    const isLinear = !!linMatch;
    const pathMatch = gradFillXml.match(/<a:path path="(circle|rect|shape)"/);
    const isRadial = !!pathMatch;
    if (!isLinear && !isRadial) {
        return null;
    }
    const gsMatches = Array.from(gradFillXml.matchAll(/<a:gs pos="(\d+)"[^>]*>(.*?)<\/a:gs>/gs));
    if (gsMatches.length === 0) {
        return null;
    }
    const stops = [];
    for (const gsMatch of gsMatches) {
        const position = parseInt(gsMatch[1]) / 100000;
        const gsContent = gsMatch[2];
        let color = null;
        const srgbMatch = gsContent.match(/<a:srgbClr val="([0-9A-Fa-f]{6})"/);
        if (srgbMatch) {
            color = '#' + srgbMatch[1];
            const alphaMatch = gsContent.match(/<a:alpha val="(\d+)"/);
            if (alphaMatch) {
                const alpha = parseInt(alphaMatch[1]) / 100000;
                stops.push({ position, color, alpha });
                continue;
            }
        }
        const schemeMatch = gsContent.match(/<a:schemeClr val="([^"]+)"[^>]*>(.*?)<\/a:schemeClr>/s);
        if (schemeMatch) {
            color = resolveThemeColor(schemeMatch[1], theme);
            const transforms = schemeMatch[2];
            if (transforms && transforms.includes('<a:')) {
                color = applyColorTransforms(color, transforms);
            }
            if (color.startsWith('rgba(')) {
                const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
                if (rgbaMatch) {
                    const r = parseInt(rgbaMatch[1]);
                    const g = parseInt(rgbaMatch[2]);
                    const b = parseInt(rgbaMatch[3]);
                    const alpha = parseFloat(rgbaMatch[4]);
                    const hexColor = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
                    stops.push({ position, color: hexColor, alpha });
                    continue;
                }
            }
        }
        if (color) {
            stops.push({ position, color });
        }
    }
    if (stops.length === 0) {
        return null;
    }
    if (isLinear) {
        const angle = linMatch ? parseInt(linMatch[1]) / 60000 : 0;
        return {
            type: 'linear',
            angle,
            stops,
        };
    }
    else {
        const path = pathMatch ? pathMatch[1] : 'circle';
        const fillToRectMatch = gradFillXml.match(/<a:fillToRect[^>]*l="(\d+)"[^>]*t="(\d+)"[^>]*r="(\d+)"[^>]*b="(\d+)"/);
        let focusX = 0.5;
        let focusY = 0.5;
        if (fillToRectMatch) {
            const l = parseInt(fillToRectMatch[1]) / 100000;
            const t = parseInt(fillToRectMatch[2]) / 100000;
            const r = parseInt(fillToRectMatch[3]) / 100000;
            const b = parseInt(fillToRectMatch[4]) / 100000;
            focusX = l + (1 - l - r) / 2;
            focusY = t + (1 - t - b) / 2;
        }
        return {
            type: 'radial',
            path,
            focusX,
            focusY,
            stops,
        };
    }
}
export function extractShapeStroke(spPrXml, theme) {
    const srgbMatch = spPrXml.match(/<a:ln[^>]*>.*?<a:solidFill>.*?<a:srgbClr val="([0-9A-Fa-f]{6})"/s);
    if (srgbMatch) {
        const color = srgbMatch[1];
        return color.startsWith('#') ? color : '#' + color;
    }
    const schemeClrFullMatch = spPrXml.match(/<a:ln[^>]*>.*?<a:solidFill>.*?<a:schemeClr val="([^"]+)"[^>]*>(.*?)<\/a:schemeClr>/s);
    if (schemeClrFullMatch) {
        const baseColor = resolveThemeColor(schemeClrFullMatch[1], theme);
        const transforms = schemeClrFullMatch[2];
        if (transforms && transforms.includes('<a:')) {
            return applyColorTransforms(baseColor, transforms);
        }
        return baseColor;
    }
    const schemeClrMatch = spPrXml.match(/<a:ln[^>]*>.*?<a:solidFill>.*?<a:schemeClr val="([^"]+)"\/>/s);
    if (schemeClrMatch) {
        return resolveThemeColor(schemeClrMatch[1], theme);
    }
    return null;
}
function customGeometryToSvgPath(geom) {
    let pathData = '';
    for (const path of geom.paths) {
        for (const cmd of path) {
            switch (cmd.type) {
                case 'M':
                    pathData += `M ${cmd.points[0]} ${cmd.points[1]} `;
                    break;
                case 'L':
                    pathData += `L ${cmd.points[0]} ${cmd.points[1]} `;
                    break;
                case 'C':
                    pathData += `C ${cmd.points[0]} ${cmd.points[1]} ${cmd.points[2]} ${cmd.points[3]} ${cmd.points[4]} ${cmd.points[5]} `;
                    break;
                case 'Z':
                    pathData += 'Z ';
                    break;
            }
        }
    }
    return pathData.trim();
}
function customGeometryToSvgDataUri(geom, fillColor) {
    const pathData = customGeometryToSvgPath(geom);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${geom.width} ${geom.height}" width="${geom.width}" height="${geom.height}">
  <path d="${pathData}" fill="${fillColor}" fill-rule="evenodd"/>
</svg>`;
    if (typeof btoa !== 'undefined') {
        return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
    }
    else {
        return 'data:image/svg+xml;base64,' + Buffer.from(svg).toString('base64');
    }
}
function parseCustomGeometry(spXml) {
    const custGeomMatch = spXml.match(/<a:custGeom[^>]*>(.*)<\/a:custGeom>/s);
    if (!custGeomMatch) {
        return undefined;
    }
    const custGeomXml = custGeomMatch[1];
    const pathListMatch = custGeomXml.match(/<a:pathLst>(.*)<\/a:pathLst>/s);
    if (!pathListMatch) {
        return undefined;
    }
    const pathListXml = pathListMatch[1];
    const pathMatches = Array.from(pathListXml.matchAll(/<a:path[^>]*w="(\d+)"[^>]*h="(\d+)"[^>]*>(.*?)<\/a:path>/gs));
    if (pathMatches.length === 0) {
        return undefined;
    }
    const width = parseInt(pathMatches[0][1]);
    const height = parseInt(pathMatches[0][2]);
    const paths = [];
    for (const pathMatch of pathMatches) {
        const pathXml = pathMatch[3];
        const commands = [];
        const commandRegex = /<a:(moveTo|lnTo|cubicBezTo|close)[^>]*>(.*?)<\/a:\1>|<a:close\/>/g;
        const commandMatches = Array.from(pathXml.matchAll(commandRegex));
        for (const cmdMatch of commandMatches) {
            const cmdType = cmdMatch[1];
            const cmdContent = cmdMatch[2] || '';
            if (cmdType === 'moveTo') {
                const ptMatch = cmdContent.match(/<a:pt x="(\d+)" y="(\d+)"\/>/);
                if (ptMatch) {
                    commands.push({
                        type: 'M',
                        points: [parseInt(ptMatch[1]), parseInt(ptMatch[2])],
                    });
                }
            }
            else if (cmdType === 'lnTo') {
                const ptMatch = cmdContent.match(/<a:pt x="(\d+)" y="(\d+)"\/>/);
                if (ptMatch) {
                    commands.push({
                        type: 'L',
                        points: [parseInt(ptMatch[1]), parseInt(ptMatch[2])],
                    });
                }
            }
            else if (cmdType === 'cubicBezTo') {
                const ptMatches = Array.from(cmdContent.matchAll(/<a:pt x="(\d+)" y="(\d+)"\/>/g));
                if (ptMatches.length === 3) {
                    commands.push({
                        type: 'C',
                        points: [
                            parseInt(ptMatches[0][1]), parseInt(ptMatches[0][2]),
                            parseInt(ptMatches[1][1]), parseInt(ptMatches[1][2]),
                            parseInt(ptMatches[2][1]), parseInt(ptMatches[2][2]),
                        ],
                    });
                }
            }
            else if (cmdMatch[0] === '<a:close/>') {
                commands.push({ type: 'Z', points: [] });
            }
        }
        if (commands.length > 0) {
            paths.push(commands);
        }
    }
    return { width, height, paths };
}
function extractFillColor(spXml, theme) {
    const spPrXml = spXml;
    const srgbMatch = spPrXml.match(/<a:solidFill>.*?<a:srgbClr val="([0-9A-Fa-f]{6})"/s);
    if (srgbMatch) {
        return '#' + srgbMatch[1];
    }
    const schemeClrMatch = spPrXml.match(/<a:solidFill>.*?<a:schemeClr val="([^"]+)"/s);
    if (schemeClrMatch) {
        return resolveThemeColor(schemeClrMatch[1], theme);
    }
    const fillRefMatch = spXml.match(/<a:fillRef[^>]*>.*?<a:schemeClr val="([^"]+)"/s);
    if (fillRefMatch) {
        return resolveThemeColor(fillRefMatch[1], theme);
    }
    return undefined;
}
function parseGroupShape(grpXml, theme, _mediaMap) {
    const grpXfrm = grpXml.match(/<p:grpSpPr>.*?<a:xfrm[^>]*>(.*?)<\/a:xfrm>/s);
    let grpX = 0, grpY = 0, grpWidth = 0, grpHeight = 0;
    if (grpXfrm) {
        const xMatch = grpXfrm[1].match(/<a:off[^>]*x="(-?\d+)"/);
        const yMatch = grpXfrm[1].match(/<a:off[^>]*y="(-?\d+)"/);
        const widthMatch = grpXfrm[1].match(/<a:ext[^>]*cx="(-?\d+)"/);
        const heightMatch = grpXfrm[1].match(/<a:ext[^>]*cy="(-?\d+)"/);
        if (xMatch) {
            grpX = parseInt(xMatch[1]) / 12700;
        }
        if (yMatch) {
            grpY = parseInt(yMatch[1]) / 12700;
        }
        if (widthMatch) {
            grpWidth = parseInt(widthMatch[1]) / 12700;
        }
        if (heightMatch) {
            grpHeight = parseInt(heightMatch[1]) / 12700;
        }
    }
    const children = [];
    const nestedSpXmls = [];
    let searchPos = 0;
    while (true) {
        const startMatch = grpXml.substring(searchPos).match(/<p:sp(?:>|\s[^>]*>)/);
        if (!startMatch)
            break;
        const startPos = searchPos + startMatch.index;
        const contentStart = startPos + startMatch[0].length;
        let depth = 1;
        let pos = contentStart;
        while (depth > 0 && pos < grpXml.length) {
            const nextOpen = grpXml.indexOf('<p:sp>', pos);
            const nextOpenAttr = grpXml.indexOf('<p:sp ', pos);
            const nextClose = grpXml.indexOf('</p:sp>', pos);
            if (nextClose === -1)
                break;
            let actualNextOpen = -1;
            if (nextOpen !== -1 && nextOpenAttr !== -1) {
                actualNextOpen = Math.min(nextOpen, nextOpenAttr);
            }
            else if (nextOpen !== -1) {
                actualNextOpen = nextOpen;
            }
            else if (nextOpenAttr !== -1) {
                actualNextOpen = nextOpenAttr;
            }
            if (actualNextOpen !== -1 && actualNextOpen < nextClose) {
                depth++;
                pos = actualNextOpen + 5;
            }
            else {
                depth--;
                pos = nextClose + 7;
            }
        }
        if (depth === 0) {
            const extracted = grpXml.substring(contentStart, pos - 7);
            nestedSpXmls.push(extracted);
            searchPos = pos;
        }
        else {
            break;
        }
    }
    for (const spXml of nestedSpXmls) {
        const xfrm = spXml.match(/<a:xfrm[^>]*>(.*?)<\/a:xfrm>/s);
        if (!xfrm) {
            continue;
        }
        const xMatch = xfrm[1].match(/<a:off[^>]*x="(-?\d+)"/);
        const yMatch = xfrm[1].match(/<a:off[^>]*y="(-?\d+)"/);
        const widthMatch = xfrm[1].match(/<a:ext[^>]*cx="(-?\d+)"/);
        const heightMatch = xfrm[1].match(/<a:ext[^>]*cy="(-?\d+)"/);
        if (!xMatch || !yMatch || !widthMatch || !heightMatch) {
            continue;
        }
        const x = parseInt(xMatch[1]) / 12700;
        const y = parseInt(yMatch[1]) / 12700;
        const width = parseInt(widthMatch[1]) / 12700;
        const height = parseInt(heightMatch[1]) / 12700;
        const spPrMatch = spXml.match(/<p:spPr[^>]*>(.*)<\/p:spPr>/s);
        const spPrXml = spPrMatch ? spPrMatch[1] : '';
        const customGeometry = parseCustomGeometry(spXml);
        const fill = extractFillColor(spXml, theme) || extractFillColor(spPrXml, theme);
        const stroke = extractShapeStroke(spPrXml, theme);
        let geometry = undefined;
        const geomMatch = spPrXml.match(/<a:prstGeom prst="([^"]+)"/);
        if (geomMatch) {
            geometry = geomMatch[1];
        }
        if (customGeometry || fill || stroke || geometry) {
            let imageUrl = undefined;
            if (customGeometry && fill) {
                const closePathCount = customGeometry.paths.reduce((sum, path) => sum + path.filter(cmd => cmd.type === 'Z').length, 0);
                if (closePathCount > 1) {
                    let fillColor = '#FFFFFF';
                    if (typeof fill === 'string') {
                        fillColor = fill;
                    }
                    else if (typeof fill === 'object' && 'r' in fill) {
                        const colorObj = fill;
                        fillColor = `rgb(${colorObj.r},${colorObj.g},${colorObj.b})`;
                    }
                    imageUrl = customGeometryToSvgDataUri(customGeometry, fillColor);
                }
            }
            const shape = {
                type: imageUrl ? 'image' : 'shape',
                position: { x, y },
                size: { width, height },
                customGeometry: imageUrl ? undefined : customGeometry,
                fill: imageUrl ? undefined : (fill || undefined),
                stroke: stroke || undefined,
                geometry,
                imageUrl,
            };
            children.push(shape);
        }
    }
    if (children.length === 0) {
        return null;
    }
    return {
        type: 'group',
        position: { x: grpX, y: grpY },
        size: { width: grpWidth, height: grpHeight },
        children,
    };
}
function parseTable(frameXml, _theme) {
    const tblMatch = frameXml.match(/<a:tbl[^>]*>(.*?)<\/a:tbl>/s);
    if (!tblMatch) {
        return null;
    }
    const tblXml = tblMatch[1];
    const columnWidths = [];
    const gridColMatches = Array.from(tblXml.matchAll(/<a:gridCol[^>]*w="(\d+)"/g));
    for (const match of gridColMatches) {
        columnWidths.push(parseInt(match[1]) / 12700);
    }
    const rows = [];
    const rowMatches = Array.from(tblXml.matchAll(/<a:tr[^>]*h="(\d+)"[^>]*>(.*?)<\/a:tr>/gs));
    for (const rowMatch of rowMatches) {
        const rowHeight = parseInt(rowMatch[1]) / 12700;
        const rowXml = rowMatch[2];
        const cells = [];
        const cellMatches = Array.from(rowXml.matchAll(/<a:tc>(.*?)<\/a:tc>/gs));
        for (const cellMatch of cellMatches) {
            const cellXml = cellMatch[1];
            let cellText = '';
            const textMatches = Array.from(cellXml.matchAll(/<a:t>([^<]*)<\/a:t>/g));
            for (const tMatch of textMatches) {
                cellText += decodeHtmlEntities(tMatch[1]);
            }
            let cellFill;
            const fillMatch = cellXml.match(/<a:tcPr[^>]*>.*?<a:solidFill>.*?<a:srgbClr val="([^"]+)"/s);
            if (fillMatch) {
                cellFill = '#' + fillMatch[1];
            }
            const tcPrMatch = cellXml.match(/<a:tcPr([^>]*)>/);
            let margins = { left: 7.5, right: 7.5, top: 7.5, bottom: 0 };
            if (tcPrMatch) {
                const attrs = tcPrMatch[1];
                const marLMatch = attrs.match(/marL="(\d+)"/);
                const marRMatch = attrs.match(/marR="(\d+)"/);
                const marTMatch = attrs.match(/marT="(\d+)"/);
                const marBMatch = attrs.match(/marB="(\d+)"/);
                if (marLMatch)
                    margins.left = parseInt(marLMatch[1]) / 12700;
                if (marRMatch)
                    margins.right = parseInt(marRMatch[1]) / 12700;
                if (marTMatch)
                    margins.top = parseInt(marTMatch[1]) / 12700;
                if (marBMatch)
                    margins.bottom = parseInt(marBMatch[1]) / 12700;
            }
            let verticalAnchor = 'bottom';
            const anchorMatch = cellXml.match(/anchor="([^"]+)"/);
            if (anchorMatch) {
                const anchorVal = anchorMatch[1];
                if (anchorVal === 't')
                    verticalAnchor = 'top';
                else if (anchorVal === 'ctr')
                    verticalAnchor = 'middle';
                else if (anchorVal === 'b')
                    verticalAnchor = 'bottom';
            }
            const boldMatch = cellXml.match(/b="1"/);
            const italicMatch = cellXml.match(/i="1"/);
            const sizeMatch = cellXml.match(/sz="(\d+)"/);
            const colorMatch = cellXml.match(/<a:solidFill>.*?<a:srgbClr val="([^"]+)"/s);
            const fontMatch = cellXml.match(/typeface="([^"]+)"/);
            const alignMatch = cellXml.match(/algn="([^"]+)"/);
            let textAlign = 'left';
            if (alignMatch) {
                const alignVal = alignMatch[1];
                if (alignVal === 'ctr')
                    textAlign = 'center';
                else if (alignVal === 'r')
                    textAlign = 'right';
                else if (alignVal === 'l')
                    textAlign = 'left';
            }
            cells.push({
                text: cellText,
                fill: cellFill,
                margins,
                verticalAnchor,
                textStyle: {
                    bold: !!boldMatch,
                    italic: !!italicMatch,
                    fontSize: sizeMatch ? parseInt(sizeMatch[1]) / 100 : 14,
                    color: colorMatch ? '#' + colorMatch[1] : '#000000',
                    fontFamily: fontMatch ? fontMatch[1] : 'Calibri',
                    align: textAlign,
                },
            });
        }
        rows.push({
            height: rowHeight,
            cells,
        });
    }
    return {
        rows,
        columnWidths,
    };
}
export function parseSlideXml(slideXml, theme, mediaMap, _isLayout, layoutXml, slideNumber) {
    const shapes = [];
    let background;
    const spMatches = Array.from(slideXml.matchAll(/<p:sp(?:>|\s[^>]*>)(.*?)<\/p:sp>/gs));
    const picMatches = Array.from(slideXml.matchAll(/<p:pic[^>]*>(.*?)<\/p:pic>/gs));
    const grpSpMatches = Array.from(slideXml.matchAll(/<p:grpSp[^>]*>(.*?)<\/p:grpSp>/gs));
    const cxnSpMatches = Array.from(slideXml.matchAll(/<p:cxnSp[^>]*>(.*?)<\/p:cxnSp>/gs));
    const graphicFrameMatches = Array.from(slideXml.matchAll(/<p:graphicFrame[^>]*>(.*?)<\/p:graphicFrame>/gs));
    const bgMatch = slideXml.match(/<p:bg[^>]*>(.*?)<\/p:bg>/s);
    if (bgMatch) {
        const bgXml = bgMatch[1];
        const bgBlipFillMatch = bgXml.match(/<a:blipFill[^>]*>(.*?)<\/a:blipFill>/s);
        if (bgBlipFillMatch && mediaMap) {
            const blipFillXml = bgBlipFillMatch[1];
            const rIdMatch = blipFillXml.match(/r:embed="([^"]+)"/);
            if (rIdMatch) {
                const rId = rIdMatch[1];
                const imageUrl = mediaMap[rId];
                if (imageUrl) {
                    const bgImageShape = {
                        type: 'image',
                        x: 0,
                        y: 0,
                        width: 960,
                        height: 540,
                        imageUrl,
                        isBackgroundImage: true,
                    };
                    shapes.push(bgImageShape);
                }
            }
        }
        const bgGradFillMatch = bgXml.match(/<a:gradFill[^>]*>(.*?)<\/a:gradFill>/s);
        if (bgGradFillMatch) {
            const gradient = parseGradientFill(bgGradFillMatch[1], theme);
            if (gradient) {
                background = gradient;
            }
        }
        if (!background) {
            const srgbMatch = bgXml.match(/<a:srgbClr val="([^"]+)"/);
            if (srgbMatch) {
                const color = srgbMatch[1];
                background = color.startsWith('#') ? color : '#' + color;
            }
        }
        if (!background) {
            const schemeClrMatch = bgXml.match(/<a:schemeClr val="([^"]+)"/);
            if (schemeClrMatch) {
                background = resolveThemeColor(schemeClrMatch[1], theme);
            }
        }
        if (!background) {
            const bgPrMatch = bgXml.match(/<p:bgPr[^>]*>(.*?)<\/p:bgPr>/s);
            if (bgPrMatch) {
                const bgPrXml = bgPrMatch[1];
                const bgPrGradMatch = bgPrXml.match(/<a:gradFill[^>]*>(.*?)<\/a:gradFill>/s);
                if (bgPrGradMatch) {
                    const gradient = parseGradientFill(bgPrGradMatch[1], theme);
                    if (gradient) {
                        background = gradient;
                    }
                }
                if (!background) {
                    const bgPrSolidMatch = bgPrXml.match(/<a:solidFill>.*?<a:schemeClr val="([^"]+)"/s);
                    if (bgPrSolidMatch) {
                        background = resolveThemeColor(bgPrSolidMatch[1], theme);
                    }
                }
            }
        }
    }
    for (const spMatch of spMatches) {
        const spXml = spMatch[1];
        const hasTextBody = spXml.includes('<p:txBody>');
        const hasBlip = spXml.includes('<a:blip');
        const xfrm = spXml.match(/<a:xfrm([^>]*)>(.*?)<\/a:xfrm>/s);
        let x = 0;
        let y = 0;
        let width = 0;
        let height = 0;
        let rotation = undefined;
        if (xfrm) {
            const rotMatch = xfrm[1].match(/rot="(-?\d+)"/);
            if (rotMatch) {
                rotation = parseInt(rotMatch[1]) / 60000;
            }
            const xMatch = xfrm[2].match(/<a:off[^>]*x="(-?\d+)"/);
            const yMatch = xfrm[2].match(/<a:off[^>]*y="(-?\d+)"/);
            const widthMatch = xfrm[2].match(/<a:ext[^>]*cx="(-?\d+)"/);
            const heightMatch = xfrm[2].match(/<a:ext[^>]*cy="(-?\d+)"/);
            if (!xMatch || !yMatch || !widthMatch || !heightMatch) {
                continue;
            }
            x = parseInt(xMatch[1]) / 12700;
            y = parseInt(yMatch[1]) / 12700;
            width = parseInt(widthMatch[1]) / 12700;
            height = parseInt(heightMatch[1]) / 12700;
        }
        const phMatch = spXml.match(/<p:ph([^>]*)>/);
        let placeholderType = null;
        let placeholderIdx = undefined;
        if (phMatch) {
            const phAttrs = phMatch[1];
            const phTypeMatch = phAttrs.match(/type="([^"]+)"/);
            const phIdxMatch = phAttrs.match(/idx="(\d+)"/);
            placeholderType = phTypeMatch ? phTypeMatch[1] : null;
            placeholderIdx = phIdxMatch ? parseInt(phIdxMatch[1]) : undefined;
        }
        let layoutLstStyle = undefined;
        if (layoutXml && (placeholderType !== null || placeholderIdx !== undefined)) {
            const phKey = `${placeholderType ?? 'null'}:${placeholderIdx ?? 'default'}`;
            const layoutShapes = Array.from(layoutXml.matchAll(/<p:sp>(.*?)<\/p:sp>/gs));
            for (const layoutSpMatch of layoutShapes) {
                const layoutSpXml = layoutSpMatch[1];
                const layoutPhMatch = layoutSpXml.match(/<p:ph([^>]*)>/);
                if (layoutPhMatch) {
                    const layoutPhAttrs = layoutPhMatch[1];
                    const layoutPhTypeMatch = layoutPhAttrs.match(/type="([^"]+)"/);
                    const layoutPhIdxMatch = layoutPhAttrs.match(/idx="(\d+)"/);
                    const layoutPhType = layoutPhTypeMatch ? layoutPhTypeMatch[1] : null;
                    const layoutPhIdx = layoutPhIdxMatch ? parseInt(layoutPhIdxMatch[1]) : undefined;
                    const layoutPhKey = `${layoutPhType ?? 'null'}:${layoutPhIdx ?? 'default'}`;
                    if (phKey === layoutPhKey) {
                        const layoutTxBodyMatch = layoutSpXml.match(/<p:txBody>(.*?)<\/p:txBody>/s);
                        if (layoutTxBodyMatch) {
                            const layoutTxBodyXml = layoutTxBodyMatch[1];
                            const layoutLstStyleMatch = layoutTxBodyXml.match(/<a:lstStyle>(.*?)<\/a:lstStyle>/s);
                            if (layoutLstStyleMatch) {
                                layoutLstStyle = layoutLstStyleMatch[1];
                            }
                        }
                        break;
                    }
                }
            }
        }
        const spPrMatch = spXml.match(/<p:spPr[^>]*>(.*?)<\/p:spPr>/s);
        let fill = null;
        let stroke = null;
        let geometry = undefined;
        if (spPrMatch) {
            const spPrFill = extractShapeFill(spPrMatch[1], theme);
            const styleFill = extractShapeFill(spXml, theme);
            fill = spPrFill || styleFill;
            stroke = extractShapeStroke(spPrMatch[1], theme);
            const geomMatch = spPrMatch[1].match(/<a:prstGeom prst="([^"]+)"/);
            if (geomMatch) {
                geometry = geomMatch[1];
            }
        }
        if (hasBlip && mediaMap) {
            const blipMatch = spXml.match(/<a:blip[^>]*r:embed="([^"]+)"/);
            if (blipMatch) {
                const rId = blipMatch[1];
                const imageUrl = mediaMap[rId];
                if (imageUrl) {
                    log.info('parseSlideXml: Found image', {
                        rId,
                        imageUrlPrefix: imageUrl?.substring(0, 60),
                        imageUrlLength: imageUrl?.length,
                        startsWithData: imageUrl?.startsWith('data:'),
                    });
                    shapes.push({
                        type: 'image',
                        x,
                        y,
                        width,
                        height,
                        imageUrl,
                        placeholderType,
                        placeholderIdx,
                        rotation,
                    });
                    continue;
                }
            }
        }
        if (fill && !hasTextBody) {
            shapes.push({
                type: 'rectangle',
                x,
                y,
                width,
                height,
                fill,
                stroke,
                geometry,
                placeholderType,
                placeholderIdx,
                rotation,
            });
            continue;
        }
        if (!hasTextBody && spXml.includes('<a:t>')) {
            console.log('[WARN] Shape has text but no txBody - might be missed!');
        }
        const txBodyMatch = spXml.match(/<p:txBody>(.*?)<\/p:txBody>/s);
        if (!txBodyMatch) {
            continue;
        }
        const txBodyXml = txBodyMatch[1];
        const paragraphMatches = Array.from(txBodyXml.matchAll(/<a:p>(.*?)<\/a:p>/gs));
        const paragraphLines = [];
        const bodyPrMatch = txBodyXml.match(/<a:bodyPr([^>]*)>/);
        let verticalAnchor = undefined;
        let margins = undefined;
        let fontScalePercent = 1.0;
        let lineSpacingReduction = 0;
        if (bodyPrMatch) {
            const bodyPrAttrs = bodyPrMatch[1];
            const anchorMatch = bodyPrAttrs.match(/anchor="(t|ctr|b|dist|just)"/);
            if (anchorMatch) {
                const anchor = anchorMatch[1];
                if (anchor === 't')
                    verticalAnchor = 'top';
                else if (anchor === 'ctr')
                    verticalAnchor = 'middle';
                else if (anchor === 'b')
                    verticalAnchor = 'bottom';
            }
            const lInsMatch = bodyPrAttrs.match(/lIns="(\d+)"/);
            const rInsMatch = bodyPrAttrs.match(/rIns="(\d+)"/);
            const tInsMatch = bodyPrAttrs.match(/tIns="(\d+)"/);
            const bInsMatch = bodyPrAttrs.match(/bIns="(\d+)"/);
            margins = {
                left: lInsMatch ? parseInt(lInsMatch[1]) / 12700 : 7.2,
                right: rInsMatch ? parseInt(rInsMatch[1]) / 12700 : 7.2,
                top: tInsMatch ? parseInt(tInsMatch[1]) / 12700 : 3.6,
                bottom: bInsMatch ? parseInt(bInsMatch[1]) / 12700 : 3.6,
            };
        }
        const normAutofitMatch = txBodyXml.match(/<a:normAutofit([^>]*)\/>/);
        if (normAutofitMatch) {
            const autofitAttrs = normAutofitMatch[1];
            const fontScaleMatch = autofitAttrs.match(/fontScale="(\d+)"/);
            if (fontScaleMatch) {
                fontScalePercent = parseInt(fontScaleMatch[1]) / 100000;
            }
            const lnSpcReductionMatch = autofitAttrs.match(/lnSpcReduction="(\d+)"/);
            if (lnSpcReductionMatch) {
                lineSpacingReduction = parseInt(lnSpcReductionMatch[1]) / 100000;
            }
        }
        let fontSize = undefined;
        let hasEmptyLstStyle = false;
        if (!_isLayout && txBodyXml.includes('<a:lstStyle/>')) {
            hasEmptyLstStyle = true;
        }
        else {
            const lstStyleMatch = txBodyXml.match(/<a:lstStyle>(.*?)<\/a:lstStyle>/s);
            if (lstStyleMatch) {
                const lstStyleXml = lstStyleMatch[1];
                if (!_isLayout && lstStyleXml.trim() === '') {
                    hasEmptyLstStyle = true;
                }
                else {
                    const lvl1Match = lstStyleXml.match(/<a:lvl1pPr[^>]*>(.*?)<\/a:lvl1pPr>/s);
                    if (lvl1Match) {
                        const lvl1Xml = lvl1Match[1];
                        const szMatch = lvl1Xml.match(/sz="(\d+)"/);
                        if (szMatch) {
                            fontSize = parseInt(szMatch[1]) / 100;
                        }
                    }
                }
            }
        }
        if (fontSize === undefined && !hasEmptyLstStyle) {
            const hasBullets = txBodyXml.includes('<a:buChar');
            fontSize = hasBullets ? 14 : 18;
        }
        let fontFamily = theme.fonts.minorFont;
        let color = null;
        let bold = false;
        let italic = false;
        let underline = undefined;
        let strikethrough = undefined;
        let baseline = undefined;
        let characterSpacing = undefined;
        let kerning = undefined;
        let capitalization = undefined;
        let lineSpacing = undefined;
        let spaceBefore = undefined;
        let spaceAfter = undefined;
        let leftMargin = undefined;
        let rightMargin = undefined;
        let firstLineIndent = undefined;
        let bulletInfo = undefined;
        let firstParagraph = true;
        const paragraphs = [];
        let shapeLstStyleDefaults = null;
        const lstStyleMatch = txBodyXml.match(/<a:lstStyle>(.*?)<\/a:lstStyle>/s);
        let lstStyleXml = '';
        if (lstStyleMatch && lstStyleMatch[1].trim()) {
            lstStyleXml = lstStyleMatch[1];
        }
        else if (layoutLstStyle) {
            lstStyleXml = layoutLstStyle;
        }
        if (lstStyleXml) {
            const lvl1Match = lstStyleXml.match(/<a:lvl1pPr[^>]*>(.*?)<\/a:lvl1pPr>/s);
            if (lvl1Match) {
                const lvl1Xml = lvl1Match[1];
                const defRPrMatch = lvl1Xml.match(/<a:defRPr([^>]*)>(.*?)<\/a:defRPr>/s);
                if (defRPrMatch) {
                    shapeLstStyleDefaults = {};
                    const defRPrAttrs = defRPrMatch[1];
                    const defRPrContent = defRPrMatch[2];
                    shapeLstStyleDefaults.color = extractTextColor(defRPrContent, theme);
                    const szMatch = defRPrAttrs.match(/sz="(\d+)"/);
                    if (szMatch) {
                        shapeLstStyleDefaults.fontSize = parseInt(szMatch[1]) / 100;
                    }
                    const boldMatch = defRPrAttrs.match(/b="1"/);
                    if (boldMatch) {
                        shapeLstStyleDefaults.bold = true;
                    }
                    const italicMatch = defRPrAttrs.match(/i="1"/);
                    if (italicMatch) {
                        shapeLstStyleDefaults.italic = true;
                    }
                    const latinMatch = defRPrContent.match(/<a:latin typeface="([^"]+)"/);
                    if (latinMatch) {
                        shapeLstStyleDefaults.fontFamily = latinMatch[1];
                    }
                }
            }
        }
        for (const pMatch of paragraphMatches) {
            const pXml = pMatch[1];
            if (firstParagraph) {
                const pPrMatch = pXml.match(/<a:pPr([^>]*)>(.*?)<\/a:pPr>/s);
                if (pPrMatch) {
                    const pPrAttrs = pPrMatch[1];
                    const pPrContent = pPrMatch[2];
                    const marLMatch = pPrAttrs.match(/marL="(\d+)"/);
                    if (marLMatch) {
                        leftMargin = parseInt(marLMatch[1]) / 12700;
                    }
                    const marRMatch = pPrAttrs.match(/marR="(\d+)"/);
                    if (marRMatch) {
                        rightMargin = parseInt(marRMatch[1]) / 12700;
                    }
                    const indentMatch = pPrAttrs.match(/indent="(-?\d+)"/);
                    if (indentMatch) {
                        firstLineIndent = parseInt(indentMatch[1]) / 12700;
                    }
                    const lnSpcMatch = pPrContent.match(/<a:lnSpc>.*?<a:spcPct val="(\d+)"/s);
                    if (lnSpcMatch) {
                        lineSpacing = parseInt(lnSpcMatch[1]) / 100000;
                    }
                    const spcBefMatch = pPrContent.match(/<a:spcBef>.*?<a:spcPts val="(\d+)"/s);
                    if (spcBefMatch) {
                        spaceBefore = parseInt(spcBefMatch[1]) / 100;
                    }
                    const spcAftMatch = pPrContent.match(/<a:spcAft>.*?<a:spcPts val="(\d+)"/s);
                    if (spcAftMatch) {
                        spaceAfter = parseInt(spcAftMatch[1]) / 100;
                    }
                }
            }
            let bullet = '';
            const buCharMatch = pXml.match(/<a:buChar char="([^"]+)"/);
            const hasBuNone = pXml.includes('<a:buNone');
            const hasPPr = pXml.includes('<a:pPr');
            if (buCharMatch) {
                bullet = buCharMatch[1] + ' ';
            }
            else if (!hasBuNone && !hasPPr) {
                const lstStyleMatch = txBodyXml.match(/<a:lstStyle>(.*?)<\/a:lstStyle>/s);
                let lstStyleXml = '';
                if (lstStyleMatch && lstStyleMatch[1].trim()) {
                    lstStyleXml = lstStyleMatch[1];
                }
                else if (layoutLstStyle) {
                    lstStyleXml = layoutLstStyle;
                }
                if (lstStyleXml) {
                    const lvl1Match = lstStyleXml.match(/<a:lvl1pPr[^>]*>(.*?)<\/a:lvl1pPr>/s);
                    if (lvl1Match) {
                        const lvl1Xml = lvl1Match[1];
                        const hasBuClr = lvl1Xml.includes('<a:buClr');
                        const lstBuCharMatch = lvl1Xml.match(/<a:buChar char="([^"]+)"/);
                        if (lstBuCharMatch) {
                            bullet = lstBuCharMatch[1] + ' ';
                        }
                        else if (hasBuClr) {
                            bullet = '● ';
                        }
                    }
                }
            }
            if (firstParagraph && buCharMatch) {
                bulletInfo = {
                    char: buCharMatch[1],
                    type: 'char',
                };
                const buFontMatch = pXml.match(/<a:buFont typeface="([^"]+)"/);
                if (buFontMatch) {
                    bulletInfo.font = buFontMatch[1];
                }
                const buSzPctMatch = pXml.match(/<a:buSzPct val="(\d+)"/);
                if (buSzPctMatch) {
                    bulletInfo.size = parseInt(buSzPctMatch[1]) / 100000;
                }
            }
            else if (firstParagraph && bullet && !buCharMatch) {
                bulletInfo = {
                    char: bullet.trim(),
                    type: 'char',
                };
            }
            let pXmlWithBreaks = pXml.replace(/<a:br[^>]*\/?>/g, '<a:r><a:t>\n</a:t></a:r>');
            if (slideNumber !== undefined) {
                pXmlWithBreaks = pXmlWithBreaks.replace(/<a:fld[^>]*type="slidenum"[^>]*>.*?<\/a:fld>/gs, (match) => {
                    const fldFormatting = match.match(/<a:rPr[^>]*>.*?<\/a:rPr>/s)?.[0] || '';
                    return `<a:r>${fldFormatting}<a:t>${slideNumber}</a:t></a:r>`;
                });
            }
            const runMatches = pXmlWithBreaks.matchAll(/<a:r>(.*?)<\/a:r>/gs);
            const textParts = [];
            const paragraphRuns = [];
            for (const runMatch of runMatches) {
                const runXml = runMatch[1];
                const textMatch = runXml.match(/<a:t>([^<]*)<\/a:t>/);
                const runText = textMatch ? decodeHtmlEntities(textMatch[1]) : '';
                if (textMatch) {
                    textParts.push(runText);
                }
                const runFontSize = (() => {
                    const szMatch = runXml.match(/sz="(\d+)"/);
                    return szMatch ? parseInt(szMatch[1]) / 100 : undefined;
                })();
                const runFontFamily = (() => {
                    const latinMatch = runXml.match(/<a:latin typeface="([^"]+)"/);
                    if (latinMatch)
                        return latinMatch[1];
                    return placeholderType === 'title' || placeholderType === 'ctrTitle'
                        ? theme.fonts.majorFont
                        : theme.fonts.minorFont;
                })();
                const runColor = (() => {
                    const rPrMatch = runXml.match(/<a:rPr[^>]*>(.*?)<\/a:rPr>/s);
                    return rPrMatch ? extractTextColor(rPrMatch[1], theme) : undefined;
                })();
                const runBold = runXml.includes('b="1"');
                const runItalic = runXml.includes('i="1"');
                const runUnderline = (() => {
                    const uMatch = runXml.match(/u="(none|sng|dbl|heavy|dotted|dottedHeavy|dash|dashHeavy|dashLong|dashLongHeavy|dotDash|dotDashHeavy|dotDotDash|dotDotDashHeavy|wavy|wavyHeavy|wavyDbl)"/);
                    if (!uMatch)
                        return undefined;
                    const uValue = uMatch[1];
                    if (uValue === 'sng')
                        return 'single';
                    if (uValue === 'dbl')
                        return 'double';
                    if (uValue === 'wavyDbl')
                        return 'wavyDouble';
                    if (uValue !== 'none')
                        return uValue;
                    return undefined;
                })();
                const runStrikethrough = (() => {
                    const strikeMatch = runXml.match(/strike="(noStrike|sngStrike|dblStrike)"/);
                    if (!strikeMatch)
                        return undefined;
                    const strikeValue = strikeMatch[1];
                    if (strikeValue === 'sngStrike')
                        return 'single';
                    if (strikeValue === 'dblStrike')
                        return 'double';
                    return 'none';
                })();
                const runBaseline = (() => {
                    const baselineMatch = runXml.match(/baseline="(-?\d+)"/);
                    if (!baselineMatch)
                        return undefined;
                    const baselineValue = parseInt(baselineMatch[1]);
                    return (baselineValue / 1000) * (runFontSize || 18) / 100;
                })();
                const runCharacterSpacing = (() => {
                    const spcMatch = runXml.match(/spc="(-?\d+)"/);
                    return spcMatch ? parseInt(spcMatch[1]) / 12700 : undefined;
                })();
                const runCapitalization = (() => {
                    const capMatch = runXml.match(/cap="(none|all|small)"/);
                    return capMatch ? capMatch[1] : undefined;
                })();
                if (runText) {
                    paragraphRuns.push({
                        text: runText,
                        fontSize: runFontSize ?? shapeLstStyleDefaults?.fontSize,
                        fontFamily: runFontFamily,
                        color: runColor ?? shapeLstStyleDefaults?.color,
                        bold: (runBold || shapeLstStyleDefaults?.bold) || undefined,
                        italic: (runItalic || shapeLstStyleDefaults?.italic) || undefined,
                        underline: runUnderline,
                        strikethrough: runStrikethrough,
                        baseline: runBaseline,
                        characterSpacing: runCharacterSpacing,
                        capitalization: runCapitalization,
                    });
                }
                if (firstParagraph && textParts.length === 1) {
                    const szMatch = runXml.match(/sz="(\d+)"/);
                    if (szMatch) {
                        fontSize = parseInt(szMatch[1]) / 100;
                    }
                    const latinMatch = runXml.match(/<a:latin typeface="([^"]+)"/);
                    if (latinMatch) {
                        fontFamily = latinMatch[1];
                    }
                    else {
                        fontFamily = placeholderType === 'title' || placeholderType === 'ctrTitle'
                            ? theme.fonts.majorFont
                            : theme.fonts.minorFont;
                    }
                    const rPrMatch = runXml.match(/<a:rPr[^>]*>(.*?)<\/a:rPr>/s);
                    if (rPrMatch) {
                        color = extractTextColor(rPrMatch[1], theme);
                    }
                    bold = runXml.includes('b="1"');
                    italic = runXml.includes('i="1"');
                    const uMatch = runXml.match(/u="(none|sng|dbl|heavy|dotted|dottedHeavy|dash|dashHeavy|dashLong|dashLongHeavy|dotDash|dotDashHeavy|dotDotDash|dotDotDashHeavy|wavy|wavyHeavy|wavyDbl)"/);
                    if (uMatch) {
                        const uValue = uMatch[1];
                        if (uValue === 'sng')
                            underline = 'single';
                        else if (uValue === 'dbl')
                            underline = 'double';
                        else if (uValue === 'wavyDbl')
                            underline = 'wavyDouble';
                        else if (uValue !== 'none')
                            underline = uValue;
                    }
                    const strikeMatch = runXml.match(/strike="(noStrike|sngStrike|dblStrike)"/);
                    if (strikeMatch) {
                        const strikeValue = strikeMatch[1];
                        if (strikeValue === 'sngStrike')
                            strikethrough = 'single';
                        else if (strikeValue === 'dblStrike')
                            strikethrough = 'double';
                        else
                            strikethrough = 'none';
                    }
                    const baselineMatch = runXml.match(/baseline="(-?\d+)"/);
                    if (baselineMatch) {
                        const baselineValue = parseInt(baselineMatch[1]);
                        baseline = (baselineValue / 1000) * (fontSize || 18) / 100;
                    }
                    const spcMatch = runXml.match(/spc="(-?\d+)"/);
                    if (spcMatch) {
                        characterSpacing = parseInt(spcMatch[1]) / 12700;
                    }
                    const kernMatch = runXml.match(/kern="(\d+)"/);
                    if (kernMatch) {
                        kerning = parseInt(kernMatch[1]) / 12700;
                    }
                    const capMatch = runXml.match(/cap="(none|all|small)"/);
                    if (capMatch) {
                        capitalization = capMatch[1];
                    }
                }
            }
            const paragraphText = textParts.join('');
            if (paragraphText.trim()) {
                paragraphLines.push(bullet + paragraphText);
            }
            if (paragraphRuns.length > 0 || bullet) {
                if (bullet && paragraphRuns.length > 0) {
                    paragraphRuns.unshift({
                        text: bullet,
                        fontSize: fontSize,
                        fontFamily: fontFamily,
                        color: color,
                        bold: false,
                        italic: false,
                        underline: undefined,
                        strikethrough: undefined,
                        baseline: undefined,
                        characterSpacing: undefined,
                        capitalization: undefined,
                    });
                }
                if (paragraphRuns.length === 0) {
                    firstParagraph = false;
                    continue;
                }
                const pPrMatch = pXml.match(/<a:pPr([^>]*)>(.*?)<\/a:pPr>/s);
                let pAlign = undefined;
                let pLineSpacing = undefined;
                let pSpaceBefore = undefined;
                let pSpaceAfter = undefined;
                let pLeftMargin = undefined;
                let pFirstLineIndent = undefined;
                let pLevel = undefined;
                let pBullet = undefined;
                if (pPrMatch) {
                    const pPrAttrs = pPrMatch[1];
                    const pPrContent = pPrMatch[2];
                    const lvlMatch = pPrAttrs.match(/lvl="(\d+)"/);
                    if (lvlMatch) {
                        pLevel = parseInt(lvlMatch[1]);
                    }
                    if (pPrContent.includes('<a:buNone')) {
                        pBullet = { type: 'none' };
                    }
                    else if (pPrContent.includes('<a:buAutoNum')) {
                        pBullet = { type: 'autoNum' };
                    }
                    else if (pPrContent.includes('<a:buChar')) {
                        const buCharMatch = pPrContent.match(/<a:buChar char="([^"]*)"/);
                        const buChar = buCharMatch ? buCharMatch[1] : '•';
                        const buFontMatch = pPrContent.match(/<a:buFont typeface="([^"]*)"/);
                        const buFont = buFontMatch ? buFontMatch[1] : undefined;
                        let buColor = undefined;
                        const buClrMatch = pPrContent.match(/<a:buClr>(.*?)<\/a:buClr>/s);
                        if (buClrMatch) {
                            const clrContent = buClrMatch[1];
                            const srgbMatch = clrContent.match(/val="([0-9A-F]{6})"/i);
                            if (srgbMatch) {
                                buColor = `#${srgbMatch[1]}`;
                            }
                        }
                        const buSzPctMatch = pPrContent.match(/<a:buSzPct val="(\d+)"/);
                        const buSize = buSzPctMatch ? parseInt(buSzPctMatch[1]) / 100000 : undefined;
                        pBullet = {
                            type: 'char',
                            char: buChar,
                            font: buFont,
                            color: buColor,
                            size: buSize,
                        };
                    }
                    const algnMatch = pPrAttrs.match(/algn="(l|r|ctr|just|dec)"/);
                    if (algnMatch) {
                        const algnValue = algnMatch[1];
                        if (algnValue === 'l')
                            pAlign = 'left';
                        else if (algnValue === 'r')
                            pAlign = 'right';
                        else if (algnValue === 'ctr')
                            pAlign = 'center';
                        else if (algnValue === 'just')
                            pAlign = 'justify';
                    }
                    const lnSpcMatch = pPrContent.match(/<a:lnSpc>.*?<a:spcPct val="(\d+)"/s);
                    if (lnSpcMatch) {
                        pLineSpacing = parseInt(lnSpcMatch[1]) / 100000;
                    }
                    const spcBefMatch = pPrContent.match(/<a:spcBef>.*?<a:spcPts val="(\d+)"/s);
                    if (spcBefMatch) {
                        pSpaceBefore = parseInt(spcBefMatch[1]) / 100;
                    }
                    const spcAftMatch = pPrContent.match(/<a:spcAft>.*?<a:spcPts val="(\d+)"/s);
                    if (spcAftMatch) {
                        pSpaceAfter = parseInt(spcAftMatch[1]) / 100;
                    }
                    const marLMatch = pPrAttrs.match(/marL="(\d+)"/);
                    if (marLMatch) {
                        pLeftMargin = parseInt(marLMatch[1]) / 12700;
                    }
                    const indentMatch = pPrAttrs.match(/indent="(-?\d+)"/);
                    if (indentMatch) {
                        pFirstLineIndent = parseInt(indentMatch[1]) / 12700;
                    }
                }
                paragraphs.push({
                    runs: paragraphRuns,
                    align: pAlign,
                    lineSpacing: pLineSpacing,
                    spaceBefore: pSpaceBefore,
                    spaceAfter: pSpaceAfter,
                    leftMargin: pLeftMargin,
                    firstLineIndent: pFirstLineIndent,
                    level: pLevel,
                    bullet: pBullet,
                });
            }
            firstParagraph = false;
        }
        let text = paragraphLines.join('\n');
        const defaultPlaceholderTexts = [
            'Click to edit Master title style',
            'Click to edit Master title',
            'Click to edit Master text styles',
            'Click to edit Master subtitle style',
            'Click to add title',
            'Click to add text',
            'Click to add subtitle',
        ];
        const isDefaultPlaceholder = defaultPlaceholderTexts.some(defaultText => text.startsWith(defaultText));
        if (isDefaultPlaceholder) {
            text = '';
        }
        if (color === null) {
            color = getDefaultTextColor(placeholderType, theme, background, fill);
        }
        if (color && typeof color === 'string' && !color.startsWith('#')) {
            color = '#' + color;
        }
        let align = undefined;
        const algnMatch = txBodyXml.match(/<a:pPr[^>]*algn="(l|ctr|r|dec)"/);
        if (algnMatch) {
            const algnValue = algnMatch[1];
            if (algnValue === 'ctr')
                align = 'center';
            else if (algnValue === 'r')
                align = 'right';
            else
                align = 'left';
        }
        else {
            const lvl1Match = txBodyXml.match(/<a:lvl1pPr[^>]*algn="(l|ctr|r|dec)"/);
            if (lvl1Match) {
                const algnValue = lvl1Match[1];
                if (algnValue === 'ctr')
                    align = 'center';
                else if (algnValue === 'r')
                    align = 'right';
                else
                    align = 'left';
            }
        }
        if (text.trim() || placeholderType !== null || fill) {
            if (fontScalePercent !== 1.0 && fontSize !== undefined) {
                fontSize = fontSize * fontScalePercent;
            }
            if (lineSpacingReduction > 0 && lineSpacing !== undefined) {
                lineSpacing = lineSpacing * (1 - lineSpacingReduction);
            }
            else if (lineSpacingReduction > 0 && lineSpacing === undefined) {
                lineSpacing = 1.2 * (1 - lineSpacingReduction);
            }
            let finalX = x;
            let finalY = y;
            let finalWidth = width;
            let finalHeight = height;
            if ((placeholderType === 'title' || placeholderType === 'ctrTitle') && (width === 0 || height === 0)) {
                finalX = 36;
                finalY = 28;
                finalWidth = 648;
                finalHeight = 82;
            }
            shapes.push({
                type: 'text',
                x: finalX,
                y: finalY,
                width: finalWidth,
                height: finalHeight,
                text,
                textStyle: {
                    fontSize,
                    fontFamily,
                    color,
                    bold,
                    fontWeight: bold ? 'bold' : 'normal',
                    fontStyle: italic ? 'italic' : 'normal',
                    align,
                    verticalAnchor,
                    margins,
                    underline,
                    strikethrough,
                    baseline,
                    characterSpacing,
                    kerning,
                    capitalization,
                    lineSpacing,
                    spaceBefore,
                    spaceAfter,
                    leftMargin,
                    rightMargin,
                    firstLineIndent,
                    bullet: bulletInfo,
                },
                paragraphs: paragraphs.length > 0 ? paragraphs : undefined,
                placeholderType,
                placeholderIdx,
                fill: fill || undefined,
                stroke: stroke || undefined,
                geometry,
                rotation,
            });
        }
    }
    for (const picMatch of picMatches) {
        const picXml = picMatch[1];
        const xfrm = picXml.match(/<a:xfrm([^>]*)>(.*?)<\/a:xfrm>/s);
        if (!xfrm) {
            continue;
        }
        const rotMatch = xfrm[1].match(/rot="(-?\d+)"/);
        const rotation = rotMatch ? parseInt(rotMatch[1]) / 60000 : undefined;
        const xMatch = xfrm[2].match(/<a:off[^>]*x="(-?\d+)"/);
        const yMatch = xfrm[2].match(/<a:off[^>]*y="(-?\d+)"/);
        const widthMatch = xfrm[2].match(/<a:ext[^>]*cx="(-?\d+)"/);
        const heightMatch = xfrm[2].match(/<a:ext[^>]*cy="(-?\d+)"/);
        if (!xMatch || !yMatch || !widthMatch || !heightMatch) {
            continue;
        }
        const x = parseInt(xMatch[1]) / 12700;
        const y = parseInt(yMatch[1]) / 12700;
        const width = parseInt(widthMatch[1]) / 12700;
        const height = parseInt(heightMatch[1]) / 12700;
        const blipMatch = picXml.match(/<a:blip[^>]*r:embed="([^"]+)"/);
        if (blipMatch && mediaMap) {
            const rId = blipMatch[1];
            const imageUrl = mediaMap[rId];
            if (imageUrl) {
                shapes.push({
                    type: 'image',
                    x,
                    y,
                    width,
                    height,
                    rotation,
                    imageUrl,
                });
            }
        }
    }
    for (const grpMatch of grpSpMatches) {
        const grpXml = grpMatch[1];
        const groupShape = parseGroupShape(grpXml, theme, mediaMap);
        if (groupShape) {
            shapes.push(groupShape);
        }
    }
    for (const cxnMatch of cxnSpMatches) {
        const cxnXml = cxnMatch[1];
        const xfrm = cxnXml.match(/<a:xfrm[^>]*>(.*?)<\/a:xfrm>/s);
        if (!xfrm) {
            continue;
        }
        const xMatch = xfrm[1].match(/<a:off[^>]*x="(-?\d+)"/);
        const yMatch = xfrm[1].match(/<a:off[^>]*y="(-?\d+)"/);
        const widthMatch = xfrm[1].match(/<a:ext[^>]*cx="(-?\d+)"/);
        const heightMatch = xfrm[1].match(/<a:ext[^>]*cy="(-?\d+)"/);
        if (!xMatch || !yMatch || !widthMatch || !heightMatch) {
            continue;
        }
        const x = parseInt(xMatch[1]) / 12700;
        const y = parseInt(yMatch[1]) / 12700;
        const width = parseInt(widthMatch[1]) / 12700;
        const height = parseInt(heightMatch[1]) / 12700;
        const spPrMatch = cxnXml.match(/<p:spPr[^>]*>(.*?)<\/p:spPr>/s);
        let stroke = null;
        let strokeWidth = 1;
        if (spPrMatch) {
            stroke = extractShapeStroke(spPrMatch[1], theme);
            const widthMatch = spPrMatch[1].match(/<a:ln[^>]*w="(\d+)"/);
            if (widthMatch) {
                strokeWidth = parseInt(widthMatch[1]) / 12700;
            }
        }
        shapes.push({
            type: 'line',
            x,
            y,
            width,
            height,
            stroke: stroke || undefined,
            strokeWidth,
        });
    }
    for (const frameMatch of graphicFrameMatches) {
        const frameXml = frameMatch[1];
        const xfrm = frameXml.match(/<p:xfrm[^>]*>(.*?)<\/p:xfrm>/s);
        if (!xfrm) {
            continue;
        }
        const xMatch = xfrm[1].match(/<a:off[^>]*x="(-?\d+)"/);
        const yMatch = xfrm[1].match(/<a:off[^>]*y="(-?\d+)"/);
        const widthMatch = xfrm[1].match(/<a:ext[^>]*cx="(-?\d+)"/);
        const heightMatch = xfrm[1].match(/<a:ext[^>]*cy="(-?\d+)"/);
        if (!xMatch || !yMatch || !widthMatch || !heightMatch) {
            continue;
        }
        const x = parseInt(xMatch[1]) / 12700;
        const y = parseInt(yMatch[1]) / 12700;
        const width = parseInt(widthMatch[1]) / 12700;
        const height = parseInt(heightMatch[1]) / 12700;
        if (frameXml.includes('<a:tbl')) {
            const tableData = parseTable(frameXml, theme);
            if (tableData) {
                shapes.push({
                    type: 'table',
                    x,
                    y,
                    width,
                    height,
                    table: {
                        ...tableData,
                        position: { x, y },
                        size: { width, height },
                    },
                });
            }
        }
        else if (frameXml.includes('<c:chart')) {
            shapes.push({
                type: 'chart',
                x,
                y,
                width,
                height,
            });
        }
    }
    const transformedShapes = shapes.map((shape) => ({
        ...shape,
        position: { x: shape.x || 0, y: shape.y || 0 },
        size: { width: shape.width || 0, height: shape.height || 0 },
    }));
    return { shapes: transformedShapes, background };
}
//# sourceMappingURL=pptx-xml-parser.js.map